import useEnvVariable from '@/Shared/Hook/useEnvVariable'
import type {
    RequestQueryKeyEnum,
    RouteParamKeyEnum,
} from '@/Shared/AutoGenerated/RequestFieldEnum'
import type {
    RoutePathAppEnum,
    RoutePathBlogEnum,
    RoutePathCookieConsentEnum,
    RoutePathBookingFormEnum,
    RoutePathCustomerEnum,
    RoutePathMarketingEnum,
    RoutePathHelpCenterEnum,
    RoutePathMeetingEnum,
    RoutePathFreeToolEnum,
} from '@/Shared/AutoGenerated/RoutePathEnum'

export interface UrlQueryParam {
    key: RequestQueryKeyEnum
    value: string | number
}

export interface UrlRouteParam {
    key: RouteParamKeyEnum
    value: string | number
}

export default function () {
    // --------------------------------
    // BaseApp
    // --------------------------------

    function buildUrl(
        baseUrl: string,
        path: string,
        queryParams: UrlQueryParam[],
        routeParams: UrlRouteParam[],
    ): string {
        let queryString = ''

        const urlSearchParams = new URLSearchParams([])

        queryParams.forEach((urlQueryParam: UrlQueryParam) => {
            urlSearchParams.append(
                urlQueryParam.key.toString(),
                `${urlQueryParam.value}`,
            )
        })

        if (queryParams.length > 0) {
            queryString = '?' + urlSearchParams.toString()
        }

        const parsedPath = replaceRouteParams(path, routeParams)

        return `${baseUrl}${parsedPath}${queryString}`
    }

    function replaceRouteParams(
        url: string,
        routeParams: UrlRouteParam[],
    ): string {
        if (!routeParams) {
            return url
        }

        let temp = url

        if (
            !temp.includes('{') &&
            !temp.includes('}') &&
            Object.keys(routeParams).length === 0
        ) {
            return temp
        }

        if (Object.keys(routeParams).length === 0) {
            throw new Error('Missing route params')
        }

        routeParams.forEach((urlRouteParam: UrlRouteParam) => {
            const paramExists = temp.includes(urlRouteParam.key.toString())

            if (!paramExists) {
                throw new Error(`Route param '${urlRouteParam.key}' not found.`)
            }

            const routeParam = '{' + urlRouteParam.key + '}'

            temp = temp.replace(routeParam, `${urlRouteParam.value}`)
        })

        return temp
    }

    function app(
        path: RoutePathAppEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const appUrl = useEnvVariable().getAppDomain()

        return buildUrl(appUrl, path, queryParams, routeParams)
    }

    function landingPage(
        path: RoutePathMarketingEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const landingPageUrl = useEnvVariable().getLandingPageDomain()

        return buildUrl(landingPageUrl, path, queryParams, routeParams)
    }

    function cookieConsent(
        path: RoutePathCookieConsentEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const cookieConsentUrl = useEnvVariable().getCookieConsentDomain()

        return buildUrl(cookieConsentUrl, path, queryParams, routeParams)
    }

    function blog(
        path: RoutePathBlogEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const blogUrl = useEnvVariable().getBlogDomain()

        return buildUrl(blogUrl, path, queryParams, routeParams)
    }

    function helpCenter(
        path: RoutePathHelpCenterEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const helpCenterUrl = useEnvVariable().getHelpCenterDomain()

        return buildUrl(helpCenterUrl, path, queryParams, routeParams)
    }

    // --------------------------------
    // App
    // --------------------------------

    function bookingForm(
        path: RoutePathBookingFormEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const bookingUrl = useEnvVariable().getBookingFormDomain()

        return buildUrl(bookingUrl, path, queryParams, routeParams)
    }

    function customer(
        path: RoutePathCustomerEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const customerUrl = useEnvVariable().getCustomerDomain()

        return buildUrl(customerUrl, path, queryParams, routeParams)
    }

    function meeting(
        path: RoutePathMeetingEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const meetingUrl = useEnvVariable().getMeetingDomain()

        return buildUrl(meetingUrl, path, queryParams, routeParams)
    }

    function freeTool(
        path: RoutePathFreeToolEnum,
        queryParams: UrlQueryParam[] = [],
        routeParams: UrlRouteParam[] = [],
    ): string {
        const landingPageUrl = useEnvVariable().getLandingPageDomain()

        return buildUrl(landingPageUrl, path, queryParams, routeParams)
    }

    return {
        // BaseApp
        app,
        cookieConsent,
        landingPage,
        blog,
        helpCenter,

        // App
        bookingForm,
        customer,
        meeting,
        freeTool,
    }
}
